import { create } from 'zustand';

import { closeOtherDrawers, registerDrawerCloser } from './_drawer-mutex';

import { useInspector } from '@/components/ui/inspector/hooks/useInspector';
import type { InspectorBreadcrumbItem } from '@/components/ui/inspector/types';
import { registerWorkspaceReset } from '@/lib/workspace/workspaceResetRegistry';

export interface BrandGuidelinesDialogPayload {
  brandKitId: string;
  brandKitName: string;
  guidelines: Record<string, unknown>;
  updatedAt?: string;
}

interface BrandGuidelinesDialogState {
  isOpen: boolean;
  payload: BrandGuidelinesDialogPayload | null;
  onClose?: () => void;
  open: (args: {
    guidelines: BrandGuidelinesDialogPayload;
    breadcrumbs?: InspectorBreadcrumbItem[];
    onClose?: () => void;
  }) => void;
  close: () => void;
}

/**
 * Brand guidelines dialog payload carrier. Mirrors useDocumentDialogStore —
 * calling `open()` here pushes a brand_guidelines subject onto the Inspector
 * store so GlobalInspectorMount renders BrandGuidelinesBody, which wraps
 * BrandGuidelinesCanvas.
 *
 * Used from /library when a user clicks a brand guidelines artifact row.
 */
export const useBrandGuidelinesDialogStore = create<BrandGuidelinesDialogState>((set, get) => ({
  isOpen: false,
  payload: null,
  onClose: undefined,
  open: ({ guidelines, breadcrumbs, onClose }) => {
    closeOtherDrawers('brandGuidelines');
    set({ isOpen: true, payload: guidelines, onClose });
    useInspector
      .getState()
      .openSubject(
        { kind: 'brand_guidelines', brandKitId: guidelines.brandKitId },
        { presentation: 'workspace', breadcrumbs },
      );
  },
  close: () => {
    const { onClose } = get();
    set({ isOpen: false, payload: null, onClose: undefined });
    onClose?.();
    const inspector = useInspector.getState();
    const top = inspector.stack[inspector.stack.length - 1];
    if (top?.kind === 'brand_guidelines') inspector.close();
  },
}));

registerDrawerCloser('brandGuidelines', () => useBrandGuidelinesDialogStore.getState().close());

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (
    window as unknown as { __brandGuidelinesDialogStore: typeof useBrandGuidelinesDialogStore }
  ).__brandGuidelinesDialogStore = useBrandGuidelinesDialogStore;
}

registerWorkspaceReset('brand-guidelines-dialog-store', () => {
  useBrandGuidelinesDialogStore.setState({ isOpen: false, payload: null, onClose: undefined });
  const inspector = useInspector.getState();
  const top = inspector.stack[inspector.stack.length - 1];
  if (top?.kind === 'brand_guidelines') inspector.close();
});
